/**
 * «Загрузить план»: pictures of a plan, one per floor, become a project that opens in 3D. The pictures are read as
 * they are picked, sent to the recognition service, and the job is followed until its project arrives
 * (`src/recognition`). The app's import is the model: its tips, its floor order with floor 1 at the bottom, its stop
 * confirmation and its failure dialog — another picture, drawing by hand, or trying again.
 */
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type DragEvent, type ReactNode } from 'react';
import { Icon } from '../Icon';
import { HomeDialog, type DismissDialog } from '../home/HomeDialog';
import { recognitionChecks } from '../../recognition/checks';
import { PlanRecognition, type PlanEntry, type RecognitionPhase } from '../../recognition/controller';
import { PLAN_ACCEPT, preparePlan } from '../../recognition/images';
import { recognizedName, storeRecognized } from '../../recognition/project';
import { MAX_FILE_BYTES, MAX_FLOORS, needsCheck, type FailureCode, type FloorStatus, type JobStatus, type RecognitionTransport } from '../../recognition/protocol';
import { fixed, plural, t, type TranslationKey } from '../../i18n/I18n';
import './plan-import.css';

interface Failure { title: TranslationKey; text: TranslationKey; args?: number[]; retry?: boolean; another?: boolean; manual?: boolean }

const FAILURES: Record<FailureCode, Failure> = {
  not_recognized: { title: '#RecognitionNotRecognizedTitle', text: '#RecognitionNotRecognizedText', another: true, manual: true },
  timed_out: { title: '#RecognitionTimedOutTitle', text: '#FloorPlanImportTimeout', retry: true, another: true, manual: true },
  network: { title: '#RecognitionNetworkTitle', text: '#RecognitionNetworkText', retry: true },
  busy: { title: '#RecognitionBusyTitle', text: '#RecognitionBusyText', retry: true },
  server: { title: '#RecognitionServerTitle', text: '#RecognitionServerText', retry: true, another: true },
  too_large: { title: '#RecognitionTooLargeTitle', text: '#RecognitionTooLargeText', args: [MAX_FILE_BYTES / 1024 / 1024], another: true },
  unsupported: { title: '#RecognitionUnsupportedTitle', text: '#RecognitionUnsupportedText', another: true },
  bad_image: { title: '#RecognitionBadImageTitle', text: '#RecognitionBadImageText', another: true },
  too_many: { title: '#RecognitionTooManyTitle', text: '#RecognitionTooManyText', args: [MAX_FLOORS], another: true },
  storage: { title: '#RecognitionStorageTitle', text: '#RecognitionStorageText', retry: true },
  cancelled: { title: '#RecognitionCancelledTitle', text: '#RecognitionCancelledText', another: true },
};

const FILE_ERRORS: Partial<Record<FailureCode, TranslationKey>> = {
  too_large: '#RecognitionFileTooLarge',
  bad_image: '#RecognitionFileUnreadable',
  unsupported: '#RecognitionFileUnsupported',
};

const TIPS: TranslationKey[] = [
  '#UploadFloorPlanGuidelineTip1',
  '#RecognitionTipPhotoScanScreenshot',
  '#RecognitionTipSharperImage',
  '#RecognitionTipSizesDoorsWindows',
  '#RecognitionTipRoomLabels',
];

const floorsLabel = (count: number) => plural('#RecognitionFloorCount', count);
const sizeLabel = (bytes: number) => bytes >= 1024 * 1024 ? t('#RecognitionSizeMegabytes', fixed(bytes / 1024 / 1024, 1)) : t('#RecognitionSizeKilobytes', Math.max(1, Math.round(bytes / 1024)));
const clock = (ms: number) => { const seconds = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`; };
const imageFiles = (list: FileList | null | undefined): File[] => [...(list ?? [])];
const hasFiles = (event: DragEvent) => [...event.dataTransfer.types].includes('Files');

const busy = (phase: RecognitionPhase) => phase.kind === 'uploading' || phase.kind === 'waiting' || phase.kind === 'building';

/** Where the run is, as the four steps the dialog lists; `detail` says more about the current one. */
function steps(phase: RecognitionPhase, floors: number): { label: string; state: 'done' | 'active' | 'pending'; detail?: string }[] {
  const job: JobStatus | null = phase.kind === 'waiting' ? phase.job : null;
  const assembling = job ? job.stage === 'assembling' || job.stage === 'saving' : false;
  const order = phase.kind === 'uploading' ? 0 : phase.kind === 'waiting' ? (assembling ? 2 : 1) : 3;
  const state = (index: number) => index < order ? 'done' as const : index === order ? 'active' as const : 'pending' as const;
  const running = job?.floors.find(floor => floor.state === 'running');
  const recognizing = !job ? undefined : job.state === 'queued'
    ? (job.position ? t('#RecognitionQueuePosition', job.position) : t('#RecognitionStartingSoon'))
    : job.stage === 'cleaning' ? t('#RecognitionCleaningImage') : floors > 1 && running ? t('#RecognitionFloorOfFloors', running.index + 1, floors) : undefined;
  return [
    { label: t('#RecognitionStepUploading'), state: state(0), detail: phase.kind === 'uploading' ? `${Math.round(phase.fraction * 100)} %` : undefined },
    { label: t('#RecognitionStepRecognizing'), state: state(1), detail: order === 1 ? recognizing : undefined },
    { label: t('#RecognitionStepAssembling'), state: state(2) },
    { label: t('#RecognitionStepOpening'), state: state(3), detail: phase.kind === 'building' && phase.total ? t('#RecognitionModelsProgress', phase.done, phase.total) : undefined },
  ];
}

/**
 * The bar: the upload is its first tenth, the service's work the next three quarters — its own progress when it tells,
 * else an estimate that slows as it goes and never reaches the end — and opening the project the rest.
 */
function progressValue(phase: RecognitionPhase, waited: number): number {
  if (phase.kind === 'uploading') return 2 + 8 * phase.fraction;
  if (phase.kind === 'waiting') {
    const job = phase.job;
    const share = job.progress ?? (job.state === 'queued' ? 0 : 1 - Math.exp(-waited / 1000 / (job.etaSeconds ?? 45)));
    return 10 + 75 * Math.min(0.97, share);
  }
  if (phase.kind === 'building') return phase.total ? 85 + 15 * phase.done / phase.total : 88;
  return phase.kind === 'ready' ? 100 : 0;
}

function FadeImage({ src }: { src: string }) {
  const [ready, setReady] = useState(false);
  return <img src={src} alt="" data-ready={ready} decoding="async" onLoad={() => setReady(true)} />;
}

export function PlanImportDialog({ transport, files, name = '', onClose, onOpen, onDrawManually }: {
  transport: RecognitionTransport;
  /** Pictures dropped on the workspace before the dialog opened. */
  files?: readonly File[];
  /** The name typed in «Новый проект», if the dialog came from there. */
  name?: string;
  onClose: () => void;
  onOpen: (projectId: string, name: string) => void;
  /** An empty project on the plan, when the pictures will not do. */
  onDrawManually: (name: string) => void;
}) {
  const [recognition] = useState(() => new PlanRecognition({ transport, prepare: preparePlan, build: storeRecognized,
    culture: typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'ru-RU' }));
  const state = useSyncExternalStore(recognition.subscribe, recognition.get);
  const { plans, phase } = state;
  const pick = useRef<HTMLInputElement>(null), shoot = useRef<HTMLInputElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const dismissRef = useRef<DismissDialog | null>(null);
  const moved = useRef<{ id: string; step: 1 | -1 } | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const [coarse] = useState(() => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches);
  const [now, setNow] = useState(() => Date.now());

  // Dropped pictures are read at once; the run and the pictures go with the dialog (a remount starts over).
  useEffect(() => {
    if (files?.length) recognition.add(files);
    return () => recognition.dispose();
  }, [recognition, files]);

  // The elapsed time and the estimated bar move once a second while a run is on.
  const running = busy(phase);
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  // Each screen puts the focus on its own first action, so the keyboard never lands on a button that went away — also
  // when the first pictures replace «Выбрать файл». A microtask: the dialog's own opening focus comes after this effect.
  const hasPlans = plans.length > 0;
  useEffect(() => {
    queueMicrotask(() => body.current?.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)')?.focus());
  }, [phase.kind, state.stopping, hasPlans]);

  // A moved row keeps the focus on the arrow that moved it, or on the other arrow once it reached the end.
  useLayoutEffect(() => {
    const target = moved.current;
    if (!target) return;
    moved.current = null;
    const row = body.current?.querySelector(`[data-plan="${target.id}"]`);
    const arrow = row?.querySelector<HTMLButtonElement>(`[data-step="${target.step}"]`);
    (arrow && !arrow.disabled ? arrow : row?.querySelector<HTMLButtonElement>(`[data-step="${-target.step}"]`))?.focus();
  }, [plans]);

  // The project is stored: the dialog goes and the project opens, with «Проверьте результат» waiting when it asks for it.
  useEffect(() => {
    if (phase.kind !== 'ready') return;
    const { project } = phase;
    const unread = project.floors.some(floor => floor.state !== 'ready');
    if (needsCheck(project.score) || unread) recognitionChecks.add(project.projectId, { score: project.score, floors: project.floors, onPlan: project.onPlan });
    dismissRef.current?.(() => onOpen(project.projectId, project.name));
  }, [phase, onOpen]);

  const add = (list: File[]) => { if (list.length) recognition.add(list); };
  const choose = () => pick.current?.click();
  const start = () => recognition.start(recognizedName(name, plans.map(plan => plan.name)));
  const another = () => { recognition.clear(); choose(); };
  const manual = (dismiss: DismissDialog) => dismiss(() => onDrawManually(name.trim() || t('#RecognitionMyInterior')));
  const move = (id: string, step: 1 | -1) => { moved.current = { id, step }; recognition.move(id, step); };

  const dragProps = {
    onDragEnter: (event: DragEvent) => { if (!hasFiles(event)) return; event.preventDefault(); dragDepth.current++; setDragging(true); },
    onDragOver: (event: DragEvent) => { if (!hasFiles(event)) return; event.preventDefault(); event.dataTransfer.dropEffect = phase.kind === 'choosing' ? 'copy' : 'none'; },
    onDragLeave: () => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); },
    onDrop: (event: DragEvent) => { if (!hasFiles(event)) return; event.preventDefault(); dragDepth.current = 0; setDragging(false); if (phase.kind === 'choosing') add(imageFiles(event.dataTransfer.files)); },
  };

  const failure = phase.kind === 'failed' ? FAILURES[phase.code] : null;
  const title = phase.kind === 'choosing' ? t('#UploadPlan') : failure ? t(failure.title) : t('#RecognitionRecognizingPlan');
  const blocked = plans.some(plan => plan.state === 'failed');
  const reading = plans.some(plan => plan.state === 'reading');

  return <HomeDialog title={title} className="plan-import" wide onClose={onClose}
    canDismiss={() => { if (!recognition.busy) return true; recognition.requestStop(); return false; }}>{dismiss => {
    dismissRef.current = dismiss;
    return <div ref={body} className="plan-import-body" data-phase={phase.kind} {...dragProps}
      onPaste={event => { if (phase.kind === 'choosing') add(imageFiles(event.clipboardData.files)); }}>
      <input ref={pick} type="file" accept={PLAN_ACCEPT} multiple hidden tabIndex={-1}
        onChange={event => { add(imageFiles(event.target.files)); event.target.value = ''; }} />
      <input ref={shoot} type="file" accept="image/*" capture="environment" hidden tabIndex={-1}
        onChange={event => { add(imageFiles(event.target.files)); event.target.value = ''; }} />
      {phase.kind === 'choosing' && <>
        <p className="plan-import-lead">{t('#RecognitionLead')}</p>
        {transport.demo && <p className="plan-import-demo" role="note"><Icon name="info" size={18} /><span>{t('#RecognitionDemoNote')}</span></p>}
        <div className="plan-drop" data-dragging={dragging} data-compact={plans.length > 0}>
          {plans.length === 0 ? <>
            <span className="plan-drop-symbol" aria-hidden><Icon name="upload" size={24} /></span>
            <strong>{t('#RecognitionDropHere')}</strong>
            <div className="plan-drop-buttons">
              <button type="button" className="ws-button primary" data-autofocus onClick={choose}><Icon name="upload" size={18} />{t('#RecognitionChooseFile')}</button>
              {coarse && <button type="button" className="ws-button" onClick={() => shoot.current?.click()}><Icon name="camera" size={18} />{t('#RecognitionTakePhoto')}</button>}
            </div>
            <small>{t('#RecognitionDropFormats', floorsLabel(MAX_FLOORS))}</small>
          </> : <>
            <span className="plan-drop-note">{plans.length < MAX_FLOORS ? t('#RecognitionAnotherFloorDrop') : t('#RecognitionNoMoreFloors', floorsLabel(MAX_FLOORS))}</span>
            {plans.length < MAX_FLOORS && <button type="button" className="ws-button" onClick={choose}><Icon name="plus" size={18} />{t('#RecognitionAddFloor')}</button>}
          </>}
        </div>
        {plans.length > 0 && <PlanList plans={plans} onMove={move} onRemove={id => recognition.remove(id)} />}
        {state.overflow && <p className="ws-error" role="alert">{t('#RecognitionOverflow', MAX_FLOORS)}</p>}
        {blocked && <p className="ws-error" role="alert">{t('#RecognitionRemoveFailed')}</p>}
        {plans.length === 0 && <div className="plan-tips"><strong>{t('#UploadFloorPlanGuidelineTips')}</strong><ul>{TIPS.map(tip => <li key={tip}><Icon name="check" size={16} />{t(tip)}</li>)}</ul></div>}
        <div className="dialog-actions">
          <button type="button" className="ws-button" onClick={() => dismiss()}>{t('Cancel')}</button>
          <button type="button" className="ws-button primary" data-autofocus aria-disabled={!recognition.canStart} onClick={() => { if (recognition.canStart) start(); }}>
            {reading ? t('#RecognitionReadingImages') : plans.length > 1 ? t('#RecognitionRecognizeFloors', floorsLabel(plans.length)) : t('#RecognitionRecognizePlan')}
          </button>
        </div>
        <button type="button" className="ws-text-button plan-import-manual" onClick={() => manual(dismiss)}><Icon name="pencil" size={18} />{t('#RecognitionDrawWallsManually')}</button>
      </>}
      {(running || phase.kind === 'ready') && <Progress phase={phase} plans={plans} elapsed={state.startedAt === null ? 0 : now - state.startedAt}
        waited={state.acceptedAt === null ? 0 : now - state.acceptedAt} stopping={state.stopping} demo={transport.demo}
        onStop={() => recognition.requestStop()} onKeepWaiting={() => recognition.keepWaiting()} onConfirmStop={() => recognition.stop()} />}
      {failure && phase.kind === 'failed' && <>
        <p className="plan-import-lead">{t(failure.text, ...failure.args ?? [])}</p>
        {phase.floors.length > 1 && <FloorSummary floors={phase.floors} />}
        <div className="dialog-actions">
          {failure.manual && <button type="button" className="ws-button" onClick={() => manual(dismiss)}>{t('#DrawManually')}</button>}
          {failure.another && <button type="button" className={'ws-button' + (failure.retry ? '' : ' primary')} data-autofocus={failure.retry ? undefined : true} onClick={another}>{t('#ChooseAnotherImg')}</button>}
          {failure.retry && <button type="button" className="ws-button primary" data-autofocus onClick={() => recognition.retry()}>{t('#FloorPlanImportRetry')}</button>}
          {!failure.retry && !failure.another && <button type="button" className="ws-button primary" data-autofocus onClick={() => dismiss()}>{t('Close')}</button>}
        </div>
      </>}
    </div>;
  }}</HomeDialog>;
}

function PlanList({ plans, onMove, onRemove }: { plans: PlanEntry[]; onMove: (id: string, step: 1 | -1) => void; onRemove: (id: string) => void }) {
  const many = plans.length > 1;
  // Floor 1 at the bottom, as a building stands and as the app orders the plans.
  const rows = plans.map((plan, index) => ({ plan, index })).reverse();
  return <ol className="plan-list" aria-label={many ? t('#RecognitionFloorOrderLabel') : t('#RecognitionChosenPlan')}>
    {rows.map(({ plan, index }) => <li key={plan.id} className="plan-row" data-plan={plan.id} data-state={plan.state}>
      <span className="plan-thumb">{plan.thumbnail ? <FadeImage src={plan.thumbnail} /> : plan.state === 'failed' ? <Icon name="info" size={20} /> : <span className="plan-thumb-pending" />}</span>
      <span className="plan-row-text">
        <strong>{many ? t('#RecognitionFloorNumber', index + 1) : t('#RecognitionPlan')}</strong>
        <span>{plan.state === 'failed' ? t(FILE_ERRORS[plan.error ?? 'bad_image'] ?? '#RecognitionFileUnreadable', MAX_FILE_BYTES / 1024 / 1024) : plan.state === 'reading' ? t('#RecognitionReadingImage') : `${plan.name} · ${sizeLabel(plan.size)}`}</span>
      </span>
      {many && <span className="plan-row-order">
        <button type="button" className="ws-icon" data-step={1} aria-label={t('#RecognitionMoveFloorUp', index + 1)} disabled={index === plans.length - 1} onClick={() => onMove(plan.id, 1)}><Icon name="chevronUp" size={18} /></button>
        <button type="button" className="ws-icon" data-step={-1} aria-label={t('#RecognitionMoveFloorDown', index + 1)} disabled={index === 0} onClick={() => onMove(plan.id, -1)}><Icon name="chevronDown" size={18} /></button>
      </span>}
      <button type="button" className="ws-icon" aria-label={t('#RecognitionRemovePlan', plan.name)} onClick={() => onRemove(plan.id)}><Icon name="close" size={18} /></button>
    </li>)}
  </ol>;
}

function Progress({ phase, plans, elapsed, waited, stopping, demo, onStop, onKeepWaiting, onConfirmStop }: {
  phase: RecognitionPhase; plans: PlanEntry[]; elapsed: number; waited: number; stopping: boolean; demo: boolean;
  onStop: () => void; onKeepWaiting: () => void; onConfirmStop: () => void;
}) {
  const list = steps(phase, plans.length);
  const value = progressValue(phase, waited);
  const job = phase.kind === 'waiting' ? phase.job : null;
  const active = job?.floors.find(floor => floor.state === 'running')?.index ?? (phase.kind === 'uploading' || job?.state === 'queued' ? -1 : null);
  const current = list.find(step => step.state === 'active');
  return <>
    <div className="plan-progress-plans" data-count={plans.length}>
      {plans.map((plan, index) => <span key={plan.id} className="plan-progress-plan" data-scanning={active === index || (active === null && phase.kind === 'waiting')}>
        {plan.thumbnail && <FadeImage src={plan.thumbnail} />}
        {plans.length > 1 && <small>{t('#RecognitionFloorNumber', index + 1)}</small>}
      </span>)}
    </div>
    <div className="plan-progress" role="progressbar" aria-label={t('#RecognitionProgressLabel')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value)}>
      <span style={{ transform: `scaleX(${value / 100})` }} />
    </div>
    <ol className="plan-steps">
      {list.map(step => <li key={step.label} data-state={step.state}>
        <span className="plan-step-mark" aria-hidden>{step.state === 'done' ? <Icon name="check" size={14} /> : null}</span>
        <span>{step.label}{step.detail && <small> · {step.detail}</small>}</span>
        <span className="sr-only">{step.state === 'done' ? ' ' + t('#RecognitionStepDone') : step.state === 'active' ? ' ' + t('#RecognitionStepActive') : ''}</span>
      </li>)}
    </ol>
    <p className="plan-progress-time" role="status" aria-live="polite" aria-atomic>
      <span className="sr-only">{current ? current.label + (current.detail ? ', ' + current.detail : '') + '. ' : ''}</span>
      {t('#RecognitionElapsed', clock(elapsed))} · {demo ? t('#RecognitionHintDemo') : elapsed < 60_000 ? t('#RecognitionHintUsual') : t('#RecognitionHintLong')}
    </p>
    {stopping
      ? <div className="plan-stop" role="group" aria-label={t('#RecognitionStopQuestion')}>
          <strong>{t('#RecognitionStopQuestion')}</strong>
          <p>{t('#RecognitionStopWarning')}</p>
          <div className="dialog-actions">
            <button type="button" className="ws-button" data-autofocus onClick={onKeepWaiting}>{t('#RecognitionKeepWaiting')}</button>
            <button type="button" className="ws-button danger" onClick={onConfirmStop}>{t('#StopFloorPlansImportButton')}</button>
          </div>
        </div>
      : <div className="dialog-actions"><button type="button" className="ws-button" data-autofocus disabled={phase.kind === 'ready'} onClick={onStop}>{t('#StopFloorPlansImportButton')}</button></div>}
  </>;
}

/** The rows of the app's import summary (FpBatchImport), by how each floor ended. */
const FLOOR_OUTCOMES: Record<FloorStatus['state'], TranslationKey> = {
  ready: '#FloorPlanReadyRow', not_recognized: '#FloorPlanNotRecognizedRow', timed_out: '#FloorPlanTimedOutRow',
  scale_not_matched: '#FloorPlanScaleNotMatchedRow', waiting: '#FloorPlanNotStartedRow', running: '#FloorPlanAbandonedRow',
};

export function FloorSummary({ floors }: { floors: FloorStatus[] }): ReactNode {
  return <ul className="plan-floor-summary">
    {floors.map(floor => <li key={floor.index} data-state={floor.state}>
      <Icon name={floor.state === 'ready' ? 'check' : 'info'} size={16} />{t(FLOOR_OUTCOMES[floor.state], floor.index + 1)}
    </li>)}
  </ul>;
}
